// Load / delete the sample dataset (spec 09). Lives on the home page's
// getting-started flow and on the settings page — same panel, same endpoint.
//
// One tap each way, no confirm dialog on purpose: deleting is exact — it can
// only ever remove what loading created, so there is nothing here worth
// interrupting someone about.
import { useEffect, useState } from 'react';
import { api } from '../api';

export function SampleDataPanel({ onChanged }: { onChanged?: () => void }) {
  const [loaded, setLoaded] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .sampleDataStatus()
      .then((r) => setLoaded(r.loaded))
      // A manager or a network hiccup: just don't render the panel.
      .catch(() => setLoaded(null));
  }, []);

  if (loaded === null) return null;

  const run = (action: () => Promise<unknown>, becomes: boolean) => {
    setBusy(true);
    setError(null);
    void action()
      .then(() => {
        setLoaded(becomes);
        onChanged?.();
      })
      .catch((problem: unknown) => {
        setError(problem instanceof Error ? problem.message : 'Something went wrong.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <section className="panel">
      <h1>Sample data</h1>
      {loaded ? (
        <>
          <p className="muted">
            The sample crew, plant, jobs and booked week are loaded so you can look around.
            Deleting them removes exactly what loading created; your own data is never touched,
            and none of it ever appears in an export.
          </p>
          <button
            type="button"
            className="btn-secondary mt-4"
            disabled={busy}
            onClick={() => run(api.deleteSampleData, false)}
          >
            {busy ? 'Deleting…' : 'Delete the sample data'}
          </button>
        </>
      ) : (
        <>
          <p className="muted">
            Want to see Dayboard working before you type anything in? One tap loads a working
            dataset (ten clients, a crew of ten, ten machines, ten jobs with their documents
            and a booked fortnight) and one tap removes it all whenever you like.
          </p>
          <button
            type="button"
            className="btn-secondary mt-4"
            disabled={busy}
            onClick={() => run(api.loadSampleData, true)}
          >
            {busy ? 'Loading…' : 'Load sample data'}
          </button>
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </section>
  );
}
